import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  useTheme,
  Button,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Radio as RadioIcon,
  Home as HomeIcon,
  Close as CloseIcon,
  Map as MapIcon,
  Book as BookIcon,
  AutoAwesome as AutoAwesomeIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { normalizeCityId, CITY_REGISTRY } from '../../../data/cityRegistry';
import { PASSPORT_NAV_ITEMS } from '../../../data/passportNavigation';
import { passportColors, passportLayout } from '../../../design/passportTokens';
import { useLanguage } from '../../../context/LanguageContext';

const FIELD_BLACK = '#070707';
const BRAND_ORANGE = '#D37633';
const BRAND_CYAN = '#00E3FD';

const isNavItemActive = (item, pathname) => {
  const activePath = item.activePath || item.path;
  if (item.exact) {
    return pathname === activePath;
  }
  return pathname === activePath || pathname.startsWith(`${activePath}/`);
};

export default function TogeeUniverseHeader() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const { user, userRole, signOut } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();

  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const searchParams = new URLSearchParams(location.search);
  let storedCity = 'montreal';
  try {
    storedCity = localStorage.getItem('gigPassport.city') || 'montreal';
  } catch {}
  const currentCity = normalizeCityId(searchParams.get('city') || storedCity || user?.city || 'montreal');
  const cityConfig = CITY_REGISTRY[currentCity] || { label: currentCity };
  const cityLabel = cityConfig.label || currentCity;

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const isOperator = !!user && ['admin', 'operator', 'radar_operator'].includes(user?.assignedRole || user?.role || userRole);

  const globalNavItems = [
    { label: 'Universe Main Portal', path: '/', desc: 'Explore the macro-narrative and global feeds', icon: <HomeIcon /> },
    { label: 'The Story (The 1854 Frequency)', path: '/story', desc: '1966 / 2036 temporal split, crew registers, and the 1854 Frequency', icon: <RadioIcon /> },
    { label: 'Innovation Bots', path: '/bring-any-idea', desc: 'Generative workflow & design engines', icon: <AutoAwesomeIcon /> },
    { label: 'The Journal', path: '/journal', desc: 'Narrative logs, tracking, and archival reports', icon: <BookIcon /> },
  ];
  const passportDrawerItems = [
    ...PASSPORT_NAV_ITEMS,
    {
      id: 'signal',
      path: '/passport/signal',
      icon: RadioIcon,
      key: 'gigPassport.nav.signal',
      exact: true,
    },
  ];

  const headerHeight = isPhone ? passportLayout.compactHeaderHeight : passportLayout.desktopHeaderHeight;

  return (
    <Box
      component="header"
      sx={{
        width: '100%',
        bgcolor: FIELD_BLACK,
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backdropFilter: 'blur(12px)',
        background: 'rgba(7, 7, 7, 0.85)',
      }}
    >
      <Box
        sx={{
          height: `${headerHeight}px`,
          display: 'flex',
          alignItems: 'center',
          px: { xs: 1.5, sm: 2.5, md: 4 },
          maxWidth: 1600,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            display: 'grid',
            alignItems: 'center',
            gap: { xs: 1, lg: 2 },
            // Logo / nav / controls. The outer tracks share the slack evenly so
            // the navigation sits centred rather than drifting with the width
            // of the city name.
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr) auto',
              lg: 'minmax(0, 1fr) auto minmax(0, 1fr)',
            },
          }}
        >
          {/* Brand Logo & Active Section */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 1, md: 2 }}
            sx={{ minWidth: 0, overflow: 'hidden' }}
          >
            <Button
              onClick={toggleDrawer(true)}
              sx={{
                // This is the drawer trigger, and on a phone it is one of only
                // two ways into navigation, so it needs a real touch target
                // rather than the height of its own text.
                minHeight: 44,
                px: 0.5,
                py: 0,
                ml: -0.5,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                textTransform: 'none',
                color: '#fff',
                gap: 1,
                flexShrink: 0,
                '&:hover': {
                  bgcolor: 'transparent',
                  '& .logo-icon': {
                    color: BRAND_CYAN,
                    filter: `drop-shadow(0 0 8px ${BRAND_CYAN})`,
                  },
                  '& .logo-text': {
                    color: '#fff',
                    textShadow: `0 0 10px ${alpha(BRAND_CYAN, 0.5)}`,
                  }
                }
              }}
            >
              <RadioIcon
                className="logo-icon"
                sx={{
                  color: BRAND_ORANGE,
                  fontSize: { xs: '1.2rem', sm: '1.4rem' },
                  transition: 'all 0.3s ease',
                }}
              />
              {/*
                The "| powered by TogeeVision" tag and the "// ACTIVE TOOL"
                readout both used to live here behind a 1800px query. Neither
                survived a real viewport, and both were competing for the width
                the navigation labels needed. The active section is already
                marked in the nav itself.
              */}
              <Typography
                className="logo-text"
                sx={{
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontWeight: 800,
                  fontSize: { xs: '0.85rem', sm: '1rem' },
                  letterSpacing: '0.02em',
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.2s ease',
                }}
              >
                Gig Passport
              </Typography>
            </Button>
          </Stack>

          {/* Desktop Navigation Links Inline (1200px+) */}
          {isDesktop && (
            <Stack
              direction="row"
              spacing={{ lg: 0.5, xl: 1.5 }}
              component="nav"
              aria-label="Gig Passport navigation"
              sx={{
                alignItems: 'center',
                minWidth: 0,
                justifySelf: 'center',
              }}
            >
              {/*
                Labels used to appear only above 1800px, so nearly every real
                session saw the abbreviated forms with a tooltip to explain
                them. The bar has room for the full names once the ornaments
                are gone, and a visible label needs no tooltip.
              */}
              {PASSPORT_NAV_ITEMS.map((item) => {
                const isActive = isNavItemActive(item, location.pathname);
                const Icon = item.icon;
                const label = t(item.key || `gigPassport.nav.${item.id}`);
                return (
                  <Button
                    key={item.id}
                    component={RouterLink}
                    to={`${item.path}${location.search}`}
                    aria-current={isActive ? 'page' : undefined}
                    startIcon={<Icon sx={{ fontSize: '1rem !important' }} />}
                    sx={{
                      color: isActive ? BRAND_CYAN : 'rgba(255, 255, 255, 0.62)',
                      fontFamily: '"Space Grotesk", sans-serif',
                      fontSize: '0.82rem',
                      fontWeight: isActive ? 700 : 500,
                      letterSpacing: '0.01em',
                      textTransform: 'none',
                      borderBottom: isActive ? `2px solid ${BRAND_CYAN}` : '2px solid transparent',
                      borderRadius: 0,
                      px: { lg: 1, xl: 1.5 },
                      py: 1,
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      transition: 'color 0.2s ease, border-color 0.2s ease',
                      '& .MuiButton-startIcon': { mr: 0.75 },
                      '&:hover': {
                        color: '#fff',
                        bgcolor: 'transparent',
                        borderBottomColor: isActive ? BRAND_CYAN : 'rgba(255,255,255,0.2)',
                      },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Stack>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            // Fills its track rather than sizing to content, so the track's
            // width actually bounds it and the city name can give way.
            sx={{ width: '100%', minWidth: 0, justifyContent: 'flex-end' }}
          >
            {/*
              The city is the only piece of state here worth an outline; the
              language and menu controls used to carry identical boxes, which
              made three equal-weight buttons out of one setting and two
              utilities. The name no longer truncates — dropping the "City:"
              prefix pays for showing it in full.
            */}
            <Button
              component={RouterLink}
              to={`/passport/cities${location.search}`}
              aria-label={`${t('gigPassport.nav.city')}: ${cityLabel}`}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: 2,
                px: 1.25,
                py: 0.6,
                textTransform: 'none',
                fontFamily: '"Space Grotesk", sans-serif',
                fontWeight: 700,
                fontSize: '0.78rem',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 0.75,
                whiteSpace: 'nowrap',
                // The city is the one item here that the page itself also
                // names, so it is what gives way when a long name meets a long
                // set of navigation labels. Everything else stays intact.
                minWidth: 0,
                flexShrink: 1,
                '&:hover': {
                  borderColor: alpha(BRAND_CYAN, 0.6),
                  bgcolor: alpha(BRAND_CYAN, 0.05),
                }
              }}
            >
              <MapIcon sx={{ fontSize: '0.95rem', color: BRAND_CYAN, flexShrink: 0 }} />
              {/*
                Wide enough for every name in the registry, including
                "Laurentian Borderlands". The cap is a backstop for a future
                long name rather than the routine behaviour the old 110px
                ellipsis produced, and the full name stays in the aria-label.
              */}
              <Box
                component="span"
                sx={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {cityLabel}
              </Box>
            </Button>

            {/* Language Toggle */}
            <Button
              onClick={toggleLanguage}
              aria-label={language === 'en' ? 'Switch to French' : 'Switch to English'}
              sx={{
                color: 'rgba(255, 255, 255, 0.62)',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.06em',
                borderRadius: 2,
                px: 1,
                py: 0.6,
                minWidth: 44,
                minHeight: 44,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                fontFamily: '"Space Grotesk", sans-serif',
                '&:hover': {
                  color: '#fff',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                }
              }}
            >
              {language === 'en' ? 'FR' : 'EN'}
            </Button>

            {/* Auth Action: Sign Out or Sign In */}
            {user ? (
              <Button
                onClick={signOut}
                aria-label="Sign Out"
                startIcon={<LogoutIcon sx={{ fontSize: '0.9rem !important' }} />}
                sx={{
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.03em',
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.14)',
                  px: 1.25,
                  py: 0.6,
                  minHeight: 44,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  fontFamily: '"Space Grotesk", sans-serif',
                  '&:hover': {
                    color: '#ff6b6b',
                    borderColor: 'rgba(255, 107, 107, 0.4)',
                    bgcolor: 'rgba(255, 107, 107, 0.08)',
                  }
                }}
              >
                {language === 'en' ? 'Sign Out' : 'Déconnexion'}
              </Button>
            ) : (
              <Button
                component={RouterLink}
                to="/login"
                startIcon={<LoginIcon sx={{ fontSize: '0.9rem !important' }} />}
                sx={{
                  color: BRAND_CYAN,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.03em',
                  borderRadius: 2,
                  border: `1px solid ${alpha(BRAND_CYAN, 0.4)}`,
                  px: 1.25,
                  py: 0.6,
                  minHeight: 44,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  fontFamily: '"Space Grotesk", sans-serif',
                  '&:hover': {
                    bgcolor: alpha(BRAND_CYAN, 0.1),
                    borderColor: BRAND_CYAN,
                  }
                }}
              >
                {language === 'en' ? 'Sign In' : 'Connexion'}
              </Button>
            )}

            {/* Menu Trigger */}
            <IconButton
              color="inherit"
              onClick={toggleDrawer(true)}
              aria-label="Open Universe Dock"
              sx={{
                color: 'rgba(255, 255, 255, 0.62)',
                borderRadius: 2,
                p: 0.8,
                minWidth: 44,
                minHeight: 44,
                flexShrink: 0,
                '&:hover': {
                  color: '#fff',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                }
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Tablet Contextual Sub-row (600px - 1199px) */}
      {isTablet && (
        <Box
          component="nav"
          aria-label="Gig Passport navigation"
          sx={{
            borderTop: '1px solid rgba(255, 255, 255, 0.04)',
            bgcolor: 'rgba(0, 0, 0, 0.2)',
          }}
        >
          {/*
            Five forced equal columns meant every label was clipped at the
            narrow end of the range. Letting the row wrap costs a second line
            only on the smallest tablets, and keeps the names readable.
          */}
          <Box
            sx={{
              py: 1,
              px: 2,
              maxWidth: 960,
              mx: 'auto',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 0.5,
            }}
          >
            {PASSPORT_NAV_ITEMS.map((item) => {
              const isActive = isNavItemActive(item, location.pathname);
              const Icon = item.icon;
              const label = t(item.key || `gigPassport.nav.${item.id}`);
              return (
                <Button
                  key={item.id}
                  component={RouterLink}
                  to={`${item.path}${location.search}`}
                  aria-current={isActive ? 'page' : undefined}
                  startIcon={<Icon sx={{ fontSize: '0.9rem !important' }} />}
                  sx={{
                    color: isActive ? BRAND_CYAN : 'rgba(255, 255, 255, 0.58)',
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontSize: '0.76rem',
                    fontWeight: isActive ? 700 : 500,
                    textTransform: 'none',
                    minWidth: 0,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1.5,
                    whiteSpace: 'nowrap',
                    transition: 'color 0.2s ease, background-color 0.2s ease',
                    '& .MuiButton-startIcon': { mr: 0.6, flexShrink: 0 },
                    '&:hover': {
                      color: '#fff',
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                    },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Immersive Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: { xs: 290, sm: 340 },
            background: 'linear-gradient(165deg, #0e0e0e 0%, #070707 100%)',
            color: '#fff',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
            p: { xs: 2.5, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            boxShadow: `20px 0 80px ${alpha(FIELD_BLACK, 0.9)}`,
          }
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RadioIcon sx={{ color: BRAND_CYAN, fontSize: '1.5rem' }} />
            <Typography
              sx={{
                fontFamily: '"Noto Serif", serif',
                fontStyle: 'italic',
                fontWeight: 900,
                fontSize: '1.2rem',
                letterSpacing: '-0.02em',
              }}
            >
              TogeeVision
            </Typography>
          </Stack>
          <IconButton
            onClick={toggleDrawer(false)}
            sx={{
              color: 'rgba(255,255,255,0.4)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 1.5,
              '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Typography
          sx={{
            fontFamily: '"Space Grotesk", monospace',
            fontSize: '0.68rem',
            color: passportColors.textMuted,
            fontWeight: 700,
            letterSpacing: '0.08em',
            mb: 1.5,
            textTransform: 'uppercase',
          }}
        >
          Level 1: The Universe
        </Typography>

        <List disablePadding sx={{ mb: 3 }}>
          {globalNavItems.map((item) => (
            <ListItem key={item.path} disablePadding sx={{ mb: 1.25 }}>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                onClick={toggleDrawer(false)}
                sx={{
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.04)',
                    '& .menu-icon': { color: BRAND_ORANGE },
                  }
                }}
              >
                <ListItemIcon className="menu-icon" sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 34 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 500, fontSize: '0.88rem', color: '#fff', fontFamily: '"Space Grotesk", sans-serif' }}>
                      {item.label}
                    </Typography>
                  }
                  secondary={
                    // These descriptions differ per destination, so unlike the
                    // passport list they earn their line.
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.42)', mt: 0.25, lineHeight: 1.4 }}>
                      {item.desc}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 3 }} />

        <Typography
          sx={{
            fontFamily: '"Space Grotesk", monospace',
            fontSize: '0.68rem',
            color: passportColors.textMuted,
            fontWeight: 700,
            letterSpacing: '0.08em',
            mb: 1.5,
            textTransform: 'uppercase',
          }}
        >
          Level 2: Gig Passport
        </Typography>

        <List disablePadding sx={{ mb: 3 }}>
          {passportDrawerItems.map((item) => {
            const isActive = isNavItemActive(item, location.pathname);
            const Icon = item.icon;
            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 1.25 }}>
                <ListItemButton
                  component={RouterLink}
                  to={`${item.path}${location.search}`}
                  onClick={toggleDrawer(false)}
                  aria-current={isActive ? 'page' : undefined}
                  sx={{
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    // Active state is carried by colour alone. Boxing every row
                    // meant the list read as six stacked panels rather than one
                    // list, and left nothing for the active row to say.
                    bgcolor: isActive ? alpha(BRAND_CYAN, 0.08) : 'transparent',
                    '&:hover': {
                      bgcolor: isActive ? alpha(BRAND_CYAN, 0.12) : 'rgba(255,255,255,0.04)',
                    }
                  }}
                >
                  <ListItemIcon className="menu-icon" sx={{ color: isActive ? BRAND_CYAN : 'rgba(255,255,255,0.5)', minWidth: 34 }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: isActive ? 700 : 500, fontSize: '0.88rem', color: isActive ? BRAND_CYAN : '#fff', fontFamily: '"Space Grotesk", sans-serif' }}>
                        {t(item.key || `gigPassport.nav.${item.id}`)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {/* Permission-Gated Operator Cockpit Section */}
        {isOperator && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 3 }} />
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", monospace',
                fontSize: '0.68rem',
                color: passportColors.textMuted,
                fontWeight: 700,
                letterSpacing: '0.08em',
                mb: 1.5,
                textTransform: 'uppercase',
              }}
            >
              Level 3: Operator Control
            </Typography>
            <List disablePadding>
              <ListItem disablePadding sx={{ mb: 1.25 }}>
                <ListItemButton
                  component={RouterLink}
                  to="/operator/ledger"
                  onClick={toggleDrawer(false)}
                  sx={{
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.04)',
                      '& .menu-icon': { color: passportColors.warning },
                    }
                  }}
                >
                  <ListItemIcon className="menu-icon" sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 34 }}>
                    <BookIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 500, fontSize: '0.88rem', color: '#fff', fontFamily: '"Space Grotesk", sans-serif' }}>
                        Operator Cockpit
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.42)', mt: 0.25, lineHeight: 1.4 }}>
                        Ledger console, releases & audits
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        )}

        {/* User Account / Sign Out Section */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 2 }} />
        {user ? (
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, overflowWrap: 'anywhere' }}>
              {user.displayName || user.name || user.email}
            </Typography>
            {user.email && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', overflowWrap: 'anywhere', mb: 1.5 }}>
                {user.email}
              </Typography>
            )}
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="small"
              startIcon={<LogoutIcon fontSize="small" />}
              onClick={() => {
                setDrawerOpen(false);
                signOut();
              }}
              sx={{
                borderColor: 'rgba(244,67,54,0.4)',
                color: '#ff6b6b',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: '"Space Grotesk", sans-serif',
                '&:hover': {
                  borderColor: '#f44336',
                  bgcolor: 'rgba(244,67,54,0.1)'
                }
              }}
            >
              {language === 'en' ? 'Sign Out of Gig Passport' : 'Se déconnecter de Gig Passport'}
            </Button>
          </Box>
        ) : (
          <Button
            fullWidth
            component={RouterLink}
            to="/login"
            variant="contained"
            startIcon={<LoginIcon fontSize="small" />}
            onClick={() => setDrawerOpen(false)}
            sx={{
              bgcolor: BRAND_CYAN,
              color: FIELD_BLACK,
              fontWeight: 800,
              textTransform: 'none',
              mb: 2,
              fontFamily: '"Space Grotesk", sans-serif',
              '&:hover': { bgcolor: '#fff' }
            }}
          >
            {language === 'en' ? 'Sign In to Gig Passport' : 'Se connecter à Gig Passport'}
          </Button>
        )}

        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Typography
            sx={{
              fontStyle: 'italic',
              fontSize: '0.68rem',
              color: 'rgba(255,255,255,0.3)',
              textAlign: 'center',
            }}
          >
            TogeeVision © 2026 // Keep your eyes open.
          </Typography>
        </Box>
      </Drawer>
    </Box>
  );
}
